import React ,{useState, useEffect, useRef} from 'react'
import { createPortal } from 'react-dom';
import { jwtDecode } from 'jwt-decode';
import TranslateInput, {TranslateLanguage} from '../components/KeywordTranslator';
import '../style.css';
import EverestAudioAttribution from './EverestAudioAttribution';
import ResponsiveAudioPlayer from './ResponsiveAudioPlayer';
import { resolveChapterAudio } from './chapterAudioResolver';
import BibleNoteDialog from './BibleNoteDialog';
import {
  createVerseTimingFile,
  loadVerseAudioTimings,
  normaliseTimings,
  saveVerseAudioTimings,
  saveVerseAudioTimingsToServer,
} from './verseAudioTimings';

import {
    EuiText,
    EuiTextColor,
    EuiSelect, 
    EuiSpacer,
    EuiFlyoutBody,
    EuiFlyoutHeader,
    EuiFlyoutFooter,
    EuiFlexGroup,
    EuiFlexItem,
    EuiTitle,
    EuiButton,
    EuiButtonIcon
  } from '@elastic/eui';

const parseVerseRange = value => {
    const match = String(value ?? '').trim().match(/^(\d+)(?:\s*[-－–—]\s*(\d+))?$/);
    if (!match) return null;
    return { start: Number(match[1]), end: Number(match[2] || match[1]) };
};

function Verse(props) {
    var versenumber =  props.versenumber;
    const [footerAudioTarget, setFooterAudioTarget] = useState(null);
    const [footerNoteTarget, setFooterNoteTarget] = useState(null);
    const [selectedVerseRange, setSelectedVerseRange] = useState(() => parseVerseRange(props.versenumber));
    const [isBibleNoteOpen, setIsBibleNoteOpen] = useState(false);
    const selectedVerseLabel = selectedVerseRange
      ? (selectedVerseRange.start === selectedVerseRange.end
        ? String(selectedVerseRange.start)
        : `${selectedVerseRange.start}-${selectedVerseRange.end}`)
      : '';

    useEffect(() => {
      if (!props.useFooterAudioControls || !props.footerAudioTargetId) return;
      setFooterAudioTarget(document.getElementById(props.footerAudioTargetId));
    }, [props.useFooterAudioControls, props.footerAudioTargetId]);
    useEffect(() => {
      if (!props.footerNoteTargetId) return;
      setFooterNoteTarget(document.getElementById(props.footerNoteTargetId));
    }, [props.footerNoteTargetId]);
    const isSelectedVerse = value => {
      const verse = Number(value);
      return selectedVerseRange
        && verse >= selectedVerseRange.start
        && verse <= selectedVerseRange.end;
    };
    var language = props.language;
    const isAdmin = (() => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) return false;
        const decoded = jwtDecode(token);
        const role = decoded?.role
          || decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        return String(role || '').toLowerCase() === 'admin';
      } catch {
        return false;
      }
    })();
    const [resultVerse, setResultVerse] = useState("");
    const verseContentRef = useRef(null);
    const hasPositionedInitialVerse = useRef(false);
    const [version, setVersion] = useState("unv");
    const [book, setBook] = useState(props.book);
    const [bookname, setBookName] = useState(props.bookname);
    const [chapter, setChapter] = useState(props.chapter);
    const [playKey, setPlayKey] = useState("")
    const [prevButton, setPrevButton] = useState(true);
    const [nextButton, setNextButton] = useState(true);
    const playApiURL = process.env.REACT_APP_PlayApiURL;
    const apiKey = process.env.REACT_APP_APIKey;
    const [loading, setLoading] = useState(true); // 初始化为加载状态
    const [error, setError] = useState(false); // 控制是否显示错误信息
    const [audioStatus, setAudioStatus] = useState('checking'); // checking | ready | missing | error
    const [audioSource, setAudioSource] = useState(''); // local | everest
    const audioRef = useRef(null);
    const [verseTimings, setVerseTimings] = useState([]);
    const [activeAudioVerse, setActiveAudioVerse] = useState(null);
    const [isTimingEditorOpen, setIsTimingEditorOpen] = useState(false);
    const [timingVerse, setTimingVerse] = useState('1');
    const [chapterVerseNumbers, setChapterVerseNumbers] = useState([]);
    const [timingSaveStatus, setTimingSaveStatus] = useState('idle');
    const [timingSaveMessage, setTimingSaveMessage] = useState('');

    // const apiURL = "https://bible.fhl.net/json/qb.php?"
    const apiURL = "/api/bible/verses?"
    const options = [
      { value: 'unv', text: '和合本 (CUV)' },
      // { value: 'rcuv', text: '和合本2010 (RCUV)' },
      // { value: 'cumv', text: '官話和合本 (CYMV)' },
      // { value: 'tcv2019', text: '現代中文譯本2019版 (TCV2019)' },
      { value: 'lzz', text: '呂振中譯本 (LZZ)' },   
      // { value: 'cuwv', text: '文理和合本 （CUWV)' },   
      // { value: 'jp', text: '日語聖經 （JP)' },
      { value: 'kjv', text: 'King James (KJV)' },
      { value: 'bbe', text: 'Bible in Basic English (BBE)'}
      // { value: 'esv', text: 'English Standard Version (ESV)' },
      // { value: 'asv', text: 'American Standard Version (ASV)' },
      // { value: 'web', text: 'World English Bible (WEB)' }
    ];
    const bookMax =[{book: "創", max:50},{book: "出", max:40},{book: "利", max:27},{book: "民", max:36},{book: "申", max:34},{book: "書", max:24},{book: "士", max:21},{book: "得", max:4},
                  {book: "撒上", max:31},{book: "撒下", max:24},{book: "王上", max:22},{book: "王下", max:25},{book: "代上", max:29},{book: "代下", max:36},{book: "拉", max:10},{book: "尼", max:13},
                  {book: "斯", max:10},{book: "伯", max:42},{book: "詩", max:150},{book: "箴", max:31},{book: "傳", max:12},{book: "歌", max:8},{book: "賽", max:66},{book: "耶", max:52},{book: "哀", max:5},
                  {book: "結", max:48},{book: "但", max:12},{book: "何", max:14},{book: "珥", max:3},{book: "摩", max:9},{book: "俄", max:1},{book: "拿", max:4},{book: "彌", max:7},{book: "鴻", max:3},
                  {book: "哈", max:3},{book: "番", max:3},{book: "該", max:2},{book: "亞", max:14},{book: "瑪", max:4},{book: "太", max:28},{book: "可", max:16},{book: "路", max:24},{book: "約", max:21},
                  {book: "徒", max:28},{book: "羅", max:16},{book: "林前", max:16},{book: "林後", max:13},{book: "加", max:6},{book: "弗", max:6},{book: "腓", max:4},{book: "西", max:4},{book: "帖前", max:5},
                  {book: "帖後", max:3},{book: "提前", max:6},{book: "提後", max:4},{book: "多", max:3},{book: "門", max:1},{book: "來", max:13},{book: "雅", max:5},{book: "彼前", max:5},
                  {book: "彼後", max:3},{book: "約一", max:5},{book: "約二", max:1},{book: "約三", max:1},{book: "猶", max:1},{book: "啟", max:22}]

    const [value, setValue] = useState(options[0].value);
    const [play, setPlay] = useState(false);

    useEffect(()=>{
        loadVerse();
        loadPlayKey(chapter);
    },[])

    useEffect(() => {
      if (loading || !selectedVerseRange || !verseContentRef.current) return undefined;

      if (hasPositionedInitialVerse.current) return undefined;

      const selectedVerse = verseContentRef.current.querySelector('.scripture-verse-row.is-selected');
      if (!selectedVerse) return undefined;
      hasPositionedInitialVerse.current = true;

      const frameId = window.requestAnimationFrame(() => {
        selectedVerse.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      return () => window.cancelAnimationFrame(frameId);
    }, [loading, resultVerse, selectedVerseRange?.start, selectedVerseRange?.end]);

    useEffect(() => {
      if (!verseContentRef.current) return;
      if (!activeAudioVerse) return;

      const activeVerse = verseContentRef.current.querySelector(
        `[data-verse-row="${activeAudioVerse}"]`
      );
      if (!activeVerse) return;
      activeVerse.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [activeAudioVerse, resultVerse]);

    // useEffect(() => {
    //   const checkFileStatus = async () => {
    //     const googleDriveCheckUrl = `${playKey}`;
    //     try {
    //       const response = await fetch(googleDriveCheckUrl, { method: "HEAD" });
    //       if (!response.ok) {
    //         setError(true); // 如果文件不可访问
    //       }
    //     } catch {
    //       setError(true); // 网络或其他错误
    //     }
    //   };
    //   checkFileStatus();
    // }, [playKey]);
 
    const loadVerse = async () => {
        setLoading(true);
        var resultVerseText = "";

        await fetch(apiURL + "version=" + version + "&chineses=" + book + "&chap=" + chapter)
        .then(function(response){
            return(response.json())
        })
        .then(function(data){
            var results = JSON.parse(JSON.stringify(data))
            setChapterVerseNumbers(results.record.map(record => Number(record.sec)));
            for(var i = 0; i < results.record.length; i++) {
              resultVerseText = resultVerseText + "<EuiText><span class='scripture-verse' data-verse-number='" + results.record[i].sec + "'>" + results.record[i].sec + " " + TranslateLanguage(language,results.record[i].bible_text)  + "</span></EuiText><br/>"
            }
            //console.log(JSON.stringify(results, null, 2))  
        })

        setResultVerse(resultVerseText);
        setLoading(false);
        var getBook = book;
        var maxVerse = bookMax.find( ({ book }) => book === getBook );  
        (maxVerse.max < (parseInt(chapter) +1)) && setNextButton(false);
        ((parseInt(chapter) -1) <= 0) && setPrevButton(false);
    }

    const apiResult = async (version, selectChapter) =>{
      var apiVerseResult;
      await fetch(apiURL + "version=" + version + "&chineses=" + book + "&chap=" + selectChapter)
      .then(function(response){
          return(response.json())
      })
      .then(function(data){
          apiVerseResult = JSON.parse(JSON.stringify(data))
          //console.log(JSON.stringify(apiVerseResult, null, 2))  
      })
      return apiVerseResult;
    }

    const getAllResult =(version, selectChapter) =>{
      return Promise.all([apiResult("unv", selectChapter), apiResult(version,selectChapter)])
    }

    const onChange = (e) => {
      setValue(e.target.value);
      setVersion(e.target.value)
      var selectVersion = e.target.value;
      refreshResult(selectVersion, chapter);
    }

    const refreshResult = (selectVersion, selectChapter, shouldHighlight = true)=>{
      var showBaseVersion = (selectVersion != "unv") ? "(CUV)" : "";
      var resultVerseText = "";
      setLoading(true);
      getAllResult(selectVersion, selectChapter)
        .then(([baseVerseResult, compVerseResult]) => {
          setChapterVerseNumbers(baseVerseResult.record.map(record => Number(record.sec)));
          for(var i = 0; i < baseVerseResult.record.length; i++) {
            resultVerseText = resultVerseText + "<EuiText><span class='scripture-verse' data-verse-number='" + baseVerseResult.record[i].sec + "'>" + baseVerseResult.record[i].sec + " " + TranslateLanguage(language,baseVerseResult.record[i].bible_text)  + showBaseVersion + "</span></EuiText><br/>";
            resultVerseText = (selectVersion != "unv") ? resultVerseText + "<EuiText><span class='scripture-verse' data-verse-number='" + compVerseResult.record[i].sec + "'>" + compVerseResult.record[i].sec + " " + TranslateLanguage(language,compVerseResult.record[i].bible_text)  + " (" + selectVersion.toUpperCase()  + ") " + "</span></EuiText><br/><br/>" : resultVerseText;
        }
          setResultVerse(resultVerseText);
          setLoading(false);
        })
    
    };   
    
    const nextChapter=(continuePlaying = false)=>{
      var newChapter = (parseInt(chapter) +1).toString();
      var getBook = book;
      var maxVerse = bookMax.find( ({ book }) => book === getBook);
      (maxVerse.max < (parseInt(chapter) +2)) && setNextButton(false);
      setPrevButton(true);
      setChapter(newChapter);
      refreshResult(version, newChapter, false);
      setPlay(false)
      loadPlayKey(newChapter, continuePlaying === true);
      hasPositionedInitialVerse.current = false;
      setSelectedVerseRange({ start: 1, end: 1 });
      props.onVerseChange?.({ book, chapter: newChapter, verse: 1 });
    }

    const prevChapter=()=>{
      var newChapter = (parseInt(chapter)-1).toString();
      ((parseInt(chapter) -2) <= 0) && setPrevButton(false);
      setNextButton(true);
      setChapter(newChapter);
      refreshResult(version, newChapter, false);
      setPlay(false)
      loadPlayKey(newChapter);
      hasPositionedInitialVerse.current = false;
      setSelectedVerseRange({ start: 1, end: 1 });
      props.onVerseChange?.({ book, chapter: newChapter, verse: 1 });
    }

//   const loadPlayKey = async (selectChapter) => {
//   /*
//    * 切换章节时先清除上一章的播放器状态。
//    */
//   setPlay(false);
//   setPlayKey('');
//   setError(false);
//   setAudioStatus('checking');

//   try {
//     const response = await fetch(playApiURL, {
//       method: 'POST',

//       headers: {
//         'Content-Type':
//           'application/json; charset=UTF-8',

//         Authorization:
//           `Bearer search-${apiKey}`
//       },

//       body: JSON.stringify({
//         query: book,

//         filters: {
//           all: [
//             {
//               book
//             },
//             {
//               chapter: selectChapter
//             }
//           ]
//         },

//         page: {
//           size: 1000
//         },

//         sort: {
//           id: 'asc'
//         }
//       })
//     });

//     if (!response.ok) {
//       throw new Error(
//         `Play API returned ${response.status}`
//       );
//     }

//     const data = await response.json();

//     const firstResult =
//       Array.isArray(data?.results)
//         ? data.results[0]
//         : null;

//     /*
//      * 只使用 key。
//      * 不再使用 key2 或 link2。
//      */
//     const resultKey = String(
//       firstResult?.key?.raw ?? ''
//     ).trim();

//     /*
//      * 没有 key，直接认为本章没有朗读资源。
//      */
//     if (!resultKey) {
//       setAudioStatus('missing');
//       return;
//     }

//     /*
//      * Google Drive 文件 ID 通常只包含：
//      * 字母、数字、下划线和短横线。
//      */
//     const isValidDriveFileId =
//       /^[a-zA-Z0-9_-]+$/.test(resultKey);

//     if (!isValidDriveFileId) {
//       console.warn(
//         'Invalid Google Drive audio key:',
//         resultKey
//       );

//       setAudioStatus('missing');
//       return;
//     }

//     const previewUrl =
//       `https://drive.google.com/file/d/` +
//       `${encodeURIComponent(resultKey)}/preview`;

//     setPlayKey(previewUrl);
//     setAudioStatus('ready');
//   } catch (loadError) {
//     console.error(
//       'Failed to load verse audio:',
//       loadError
//     );

//     setPlay(false);
//     setPlayKey('');
//     setError(true);
//     setAudioStatus('error');
//   }
// };

  const loadPlayKey = async (selectChapter, continuePlaying = false) => {
      setPlay(false);
      setPlayKey('');
      setError(false);
      setAudioStatus('checking');
      setAudioSource('');
      setVerseTimings([]);
      setActiveAudioVerse(null);
      setIsTimingEditorOpen(false);
      setTimingSaveStatus('idle');
      setTimingSaveMessage('');

      try {
        const audio = await resolveChapterAudio({ book, chapter: selectChapter });

        if (audio) {
          setPlayKey(audio.url);
          setAudioSource(audio.source);
          const timings = await loadVerseAudioTimings({ book, chapter: selectChapter });
          setVerseTimings(timings);
          setAudioStatus('ready');
          setPlay(continuePlaying);
          return;
        }

        setAudioStatus('missing');
      } catch (loadError) {
        console.error('Failed to check audio resource:', loadError);
        setPlay(false);
        setPlayKey('');
        setError(true);
        setAudioStatus('error');
      }
    };

    const onPlay = (e) => {
      setPlay(true)
    };

    const handleAudioTimeUpdate = event => {
      if (!verseTimings.length) return;

      const currentTime = event.currentTarget.currentTime;
      const currentTiming = verseTimings.reduce(
        (current, timing) => (timing.start <= currentTime ? timing : current),
        null
      );
      setActiveAudioVerse(currentTiming?.verse || null);
    };

    const seekAudioToVerse = (audioElement, verseNumber) => {
      const verse = Number(verseNumber);
      if (!audioElement || !Number.isInteger(verse)) return;
      if (verse === 1) {
        audioElement.currentTime = 0;
        setActiveAudioVerse(null);
        return;
      }
      if (!verseTimings.length) return;
      const timing = verseTimings.find(item => item.verse === verse);
      if (!timing) return;
      audioElement.currentTime = timing.start;
      setActiveAudioVerse(verse);
    };

    const saveTimingDraft = timings => {
      const saved = saveVerseAudioTimings({ book, chapter, timings });
      setVerseTimings(saved);
      setTimingSaveStatus('dirty');
      setTimingSaveMessage('');
      return saved;
    };

    const markCurrentVerseTime = () => {
      const currentTime = audioRef.current?.currentTime;
      const verse = Number(timingVerse);
      if (!Number.isFinite(currentTime) || !Number.isInteger(verse) || verse < 1) return;

      const saved = saveTimingDraft([
        ...verseTimings.filter(timing => timing.verse !== verse),
        { verse, start: Number(currentTime.toFixed(3)) },
      ]);
      const nextVerse = chapterVerseNumbers.find(number => number > verse);
      if (nextVerse) setTimingVerse(String(nextVerse));
      setActiveAudioVerse(saved.find(timing => timing.verse === verse)?.verse || verse);
    };

    const saveTimingsToServer = async () => {
      const token = sessionStorage.getItem('token');
      if (!token || !verseTimings.length) return;

      setTimingSaveStatus('saving');
      setTimingSaveMessage('');
      try {
        const saved = await saveVerseAudioTimingsToServer({
          book,
          chapter,
          timings: verseTimings,
          token,
        });
        setVerseTimings(saved);
        setTimingSaveStatus('saved');
        setTimingSaveMessage(TranslateLanguage(language, '已保存到服務器。'));
      } catch (saveError) {
        setTimingSaveStatus('error');
        setTimingSaveMessage(saveError?.message || TranslateLanguage(language, '保存到服務器失敗。'));
      }
    };

    const downloadTimingFile = () => {
      const content = createVerseTimingFile({ book, chapter, timings: verseTimings });
      const file = new Blob([content], { type: 'application/json' });
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${String(chapter).padStart(3, '0')}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
    };

    const importTimingFile = async event => {
      const [file] = Array.from(event.target.files || []);
      if (!file) return;

      try {
        const imported = normaliseTimings(JSON.parse(await file.text()));
        setVerseTimings(saveTimingDraft(imported));
      } catch {
        setError(true);
      } finally {
        event.target.value = '';
      }
    };

    const handleVerseContentClick = verseNumber => {
      const verse = Number(verseNumber);
      if (!Number.isInteger(verse) || verse < 1) return;
      setSelectedVerseRange({ start: verse, end: verse });
      if (audioRef.current) seekAudioToVerse(audioRef.current, verse);
      props.onVerseChange?.({ book, chapter, verse });
    };

    const renderVerseContent = () => {
      const chunks = value !== 'unv'
        ? resultVerse.split(/<br\/><br\/>/)
        : resultVerse.split(/<br\/>/);

      return chunks
        .map(chunk => chunk.trim())
        .filter(Boolean)
        .map((chunk, index) => {
          const verseNumber = chapterVerseNumbers[index] || index + 1;
          const cleaned = chunk
            .replace(/<\/?EuiText>/g, '')
            .replace(/<span class='scripture-verse' data-verse-number='\d+'>/g, '')
            .replace(/<\/span>/g, '');
          const selected = isSelectedVerse(verseNumber);
          const audioActive = activeAudioVerse === verseNumber;

          return (
            <span
              key={verseNumber}
              data-verse-row={verseNumber}
              role="button"
              tabIndex={0}
              className={`scripture-verse-row${selected ? ' is-selected' : ''}${audioActive ? ' is-audio-active' : ''}`}
              style={{
                color: audioActive ? '#003b70' : selected ? '#1f2937' : '#333',
                fontWeight: audioActive || selected ? 700 : 400,
                backgroundColor: audioActive ? '#cdeefb' : selected ? '#fff4bf' : 'transparent',
                borderColor: audioActive ? '#0077a8' : selected ? '#f2b400' : 'transparent',
              }}
              onClick={() => handleVerseContentClick(verseNumber)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleVerseContentClick(verseNumber);
                }
              }}
              dangerouslySetInnerHTML={{ __html: cleaned }}
            />
          );
        });
    };

    return (
        <>
            {loading ? (
                <p>{TranslateLanguage(language,"正在加载中...")}</p> // 加载时显示提示文本
            ) : (
                <>
          <EuiFlyoutHeader hasBorder>
            <EuiFlexGroup wrap={false} gutterSize="xs" responsive={false}>
              <EuiFlexItem grow={false}>
              <EuiTitle size="m">
                <h2 id="flyoutTitle">{TranslateLanguage(language,bookname)} {chapter}{selectedVerseLabel ? `:${selectedVerseLabel}` : ''}</h2>
              </EuiTitle>
              </EuiFlexItem>
              <EuiFlexItem><EuiSpacer size="l"></EuiSpacer></EuiFlexItem>
              <EuiFlexItem grow={false}>
              {(prevButton) && <EuiButtonIcon
                display="base"
                iconType="arrowLeft"
                size="s"
                aria-label="上一章"
                onClick={()=> prevChapter()}
              />}
              </EuiFlexItem>
              <EuiFlexItem grow={false}><EuiSpacer size="m" /></EuiFlexItem>
              <EuiFlexItem grow={false} >           
              {(nextButton) && <EuiButtonIcon
                display="base"
                iconType="arrowRight"
                size="s"
                aria-label="下一章"
                onClick={()=> nextChapter()}
              />}
              </EuiFlexItem>
        
              </EuiFlexGroup>
          </EuiFlyoutHeader>
          <EuiFlyoutBody>
            <EuiText>
              {TranslateLanguage(
                language,
                '對照經文版本：'
              )}
            </EuiText>

            <EuiFlexGroup
              alignItems="flexEnd"
              gutterSize="m"
              responsive={false}
              className="verse-toolbar"
            >
              {/* 经文版本选择 */}
              <EuiFlexItem>
                <EuiSelect
                  id="selectVersion"
                  options={options}
                  value={value}
                  onChange={(e) => onChange(e)}
                  fullWidth
                />
              </EuiFlexItem>

            </EuiFlexGroup>

            {/* 音频状态 */}
            {audioStatus === 'checking' && (
              <>
                <EuiSpacer size="s" />

                <EuiText
                  size="xs"
                  color="subdued"
                >
                  {TranslateLanguage(
                    language,
                    '正在檢查朗讀資源……'
                  )}
                </EuiText>
              </>
            )}

            {audioStatus === 'missing' && (
              <>
                <EuiSpacer size="s" />
                <EuiText size="xs" color="subdued">
                  {TranslateLanguage(language, '本章暫無朗讀資源')}
                </EuiText>
              </>
            )}

            {audioStatus === 'ready' && audioSource === 'everest' && (
              <>
                <EuiSpacer size="xs" />
                <EverestAudioAttribution language={language} />
              </>
            )}

            {audioStatus === 'ready' && audioSource === 'local' && (
              <>
                <EuiSpacer size="xs" />
                <EuiText size="xs" color="subdued">
                  {TranslateLanguage(language, '音頻由小牧人有聲聖經提供。')}
                </EuiText>
              </>
            )}

            {audioStatus === 'error' && (
              <>
                <EuiSpacer size="s" />

                <EuiText
                  size="s"
                  color="danger"
                >
                  {TranslateLanguage(
                    language,
                    '朗讀資源加載失敗，請稍後重試'
                  )}
                </EuiText>
              </>
            )}

            {/* 音频播放器 */}
            {!props.useFooterAudioControls && audioStatus === 'ready' && (
              <>
                <EuiSpacer size="s" />

                <div className="verse-audio-container">
                  <ResponsiveAudioPlayer
                    ref={audioRef}
                    key={playKey}
                    src={playKey}
                    autoPlay={play}
                    preload="metadata"
                    className="verse-audio-player"
                    onLoadedMetadata={event => seekAudioToVerse(event.currentTarget, selectedVerseRange?.start)}
                    onTimeUpdate={handleAudioTimeUpdate}
                    onSeeked={handleAudioTimeUpdate}
                    onEnded={() => {
                      setActiveAudioVerse(null);
                      if (nextButton) nextChapter(true);
                    }}
                    onError={() => {
                      setPlay(false);
                      setError(true);
                      setAudioStatus('error');
                    }}
                  />
                </div>
              </>
            )}

            {props.useFooterAudioControls && footerAudioTarget && createPortal(
              audioStatus === 'ready' ? (
                <div className="verse-audio-container verse-footer-audio-container">
                  <ResponsiveAudioPlayer
                    ref={audioRef}
                    key={playKey}
                    src={playKey}
                    autoPlay={play}
                    preload="metadata"
                    className="verse-audio-player"
                    onLoadedMetadata={event => seekAudioToVerse(event.currentTarget, selectedVerseRange?.start)}
                    onTimeUpdate={handleAudioTimeUpdate}
                    onSeeked={handleAudioTimeUpdate}
                    onEnded={() => {
                      setActiveAudioVerse(null);
                      if (nextButton) nextChapter(true);
                    }}
                    onError={() => {
                      setPlay(false);
                      setError(true);
                      setAudioStatus('error');
                    }}
                  />
                </div>
              ) : null,
              footerAudioTarget
            )}

            {isAdmin && audioStatus === 'ready' && (
              <>
                <EuiSpacer size="xs" />
                <div className="verse-audio-sync-status">
                  {verseTimings.length
                    ? TranslateLanguage(language, `已載入 ${verseTimings.length} 節同步標注；播放時會自動定位經文。`)
                    : TranslateLanguage(language, '本章尚未有逐節同步標注。')}
                  <EuiButton
                    size="s"
                    display="empty"
                    onClick={() => setIsTimingEditorOpen(open => !open)}
                  >
                    {TranslateLanguage(language, isTimingEditorOpen ? '完成標注' : '逐節標注')}
                  </EuiButton>
                </div>
              </>
            )}

            {isAdmin && isTimingEditorOpen && audioStatus === 'ready' && (
              <div className="verse-audio-timing-editor">
                <EuiText size="xs">
                  {TranslateLanguage(language, '播放到該節開始處，選取節數後按「記錄目前時間」。完成後可直接保存到服務器。')}
                </EuiText>
                <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
                  <EuiFlexItem>
                    <EuiSelect
                      aria-label="選擇經文節數"
                      options={chapterVerseNumbers.map(verse => ({ value: String(verse), text: `${verse}` }))}
                      value={timingVerse}
                      onChange={event => setTimingVerse(event.target.value)}
                      fullWidth
                    />
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButton size="s" fill disabled={!playKey} onClick={markCurrentVerseTime}>
                      {TranslateLanguage(language, '記錄目前時間')}
                    </EuiButton>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <div className="verse-audio-timing-actions">
                  <EuiButton
                    size="s"
                    fill
                    onClick={saveTimingsToServer}
                    disabled={!verseTimings.length || timingSaveStatus === 'saving'}
                    isLoading={timingSaveStatus === 'saving'}
                  >
                    {TranslateLanguage(language, '保存到服務器')}
                  </EuiButton>
                  <EuiButton size="s" onClick={downloadTimingFile} disabled={!verseTimings.length}>
                    {TranslateLanguage(language, '下載標注 JSON')}
                  </EuiButton>
                  <label className="verse-audio-import-label">
                    {TranslateLanguage(language, '匯入標注 JSON')}
                    <input type="file" accept="application/json,.json" onChange={importTimingFile} />
                  </label>
                </div>
                {timingSaveMessage && (
                  <EuiText size="xs" color={timingSaveStatus === 'error' ? 'danger' : 'success'}>
                    {timingSaveMessage}
                  </EuiText>
                )}
              </div>
            )}

            <EuiSpacer />

            <EuiText>
              <span ref={verseContentRef} className="scripture-verse-row-list">
                {renderVerseContent()}
              </span>
            </EuiText>
          </EuiFlyoutBody>

          {!props.footerNoteTargetId && <EuiFlyoutFooter className="verse-flyout-footer">
            <EuiFlexGroup
              justifyContent="flexEnd"
              alignItems="center"
              gutterSize="s"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <EuiButton size="s" onClick={() => setIsBibleNoteOpen(true)}>
                  {TranslateLanguage(language, '筆記')}
                </EuiButton>
              </EuiFlexItem>
              {props.closeModal && (
                <EuiFlexItem grow={false}>
                  <EuiButton
                    fill
                    onClick={props.closeModal}
                  >
                    {TranslateLanguage(
                      language,
                      '關閉'
                    )}
                  </EuiButton>
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </EuiFlyoutFooter>}
          {props.footerNoteTargetId && footerNoteTarget && createPortal(
            <EuiButton onClick={() => setIsBibleNoteOpen(true)}>
              {TranslateLanguage(language, '筆記')}
            </EuiButton>,
            footerNoteTarget
          )}
          {isBibleNoteOpen && <BibleNoteDialog
            book={book} bookName={bookname} chapter={Number(chapter)}
            startVerse={selectedVerseRange?.start || 1}
            endVerse={selectedVerseRange?.end || selectedVerseRange?.start || 1}
            maxVerse={Math.max(...chapterVerseNumbers, 1)}
            scriptureText={Array.from(verseContentRef.current?.querySelectorAll('[data-verse-row]') || [])
              .filter(element => Number(element.dataset.verseRow) >= (selectedVerseRange?.start || 1)
                && Number(element.dataset.verseRow) <= (selectedVerseRange?.end || selectedVerseRange?.start || 1))
              .map(element => element.textContent.trim()).join('\n').slice(0, 4000)}
            onClose={() => setIsBibleNoteOpen(false)}
          />}
          </>
            )}
        </>
    )
}

export default Verse
